/**
 * TemplateList Component
 * Display and manage notification templates
 */

import { useState } from 'react';
import { Plus, RefreshCw, Shield, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { NotificationPreviewCompact } from './NotificationPreview';
import {
  useNotificationTemplates,
  useDeleteTemplate,
  useRefreshTemplateCache,
  useUpdateTemplate,
} from '../hooks/useNotificationTemplates';
import type { NotificationTemplate } from '../types';

interface TemplateListProps {
  onCreateClick: () => void;
  onEditClick: (template: NotificationTemplate) => void;
  className?: string;
}

export function TemplateList({ onCreateClick, onEditClick, className }: TemplateListProps) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const { data: templates = [] as NotificationTemplate[], isLoading, refetch } = useNotificationTemplates(includeInactive);
  const deleteMutation = useDeleteTemplate();
  const refreshCacheMutation = useRefreshTemplateCache();
  const updateMutation = useUpdateTemplate();

  const handleDelete = async (name: string) => {
    try {
      await deleteMutation.mutateAsync(name);
      setConfirmDelete(null);
    } catch (error) {
      console.error('Failed to delete template:', error);
      alert(`删除失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const handleToggleActive = async (template: NotificationTemplate) => {
    try {
      await updateMutation.mutateAsync({
        name: template.name,
        data: { is_active: !template.is_active },
      });
    } catch (error) {
      console.error('Failed to toggle template:', error);
      alert(`操作失败: ${error instanceof Error ? error.message : '未知错误'}`);
    }
  };

  const handleRefreshCache = async () => {
    try {
      await refreshCacheMutation.mutateAsync();
      refetch();
    } catch (error) {
      console.error('Failed to refresh cache:', error);
    }
  };

  return (
    <Card className={className} padding={false}>
      <CardHeader className="px-6 pt-6 pb-0">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">通知模板</h2>
            <p className="text-sm text-gray-500 mt-1">
              管理系统通知模板，定义渠道、内容和跳转动作
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Toggle inactive */}
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={e => setIncludeInactive(e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              显示已禁用
            </label>

            {/* Refresh cache */}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleRefreshCache}
              loading={refreshCacheMutation.isPending}
              title="刷新缓存"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>

            {/* Create new */}
            <Button size="sm" onClick={onCreateClick}>
              <Plus className="w-4 h-4 mr-1" />
              新建模板
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-0 pb-0">
        {/* Loading state */}
        {isLoading && (
          <div className="py-12 text-center text-gray-500">
            <div className="animate-spin w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-sm">加载中...</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && templates.length === 0 && (
          <div className="py-12 text-center text-gray-500">
            <p className="text-sm">暂无通知模板</p>
            <Button variant="secondary" size="sm" className="mt-4" onClick={onCreateClick}>
              <Plus className="w-4 h-4 mr-1" />
              创建第一个模板
            </Button>
          </div>
        )}

        {/* Template grid - responsive layout for wide screens */}
        {!isLoading && templates.length > 0 && (
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
            {templates.map(template => (
              <div
                key={template.name}
                className={clsx(
                  'bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md hover:border-gray-300 transition-all',
                  !template.is_active && 'opacity-60 bg-gray-50'
                )}
              >
                {/* Header with title and actions */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {template.display_name}
                      </h3>
                      {template.is_system && (
                        <span title="系统模板" className="shrink-0">
                          <Shield className="w-4 h-4 text-amber-500" />
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 font-mono truncate">
                      {template.name}
                    </p>
                  </div>

                  {/* Actions dropdown area */}
                  <div className="flex items-center gap-0.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => handleToggleActive(template)}
                      disabled={updateMutation.isPending}
                      className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title={template.is_active ? '禁用' : '启用'}
                    >
                      {template.is_active ? (
                        <Eye className="w-3.5 h-3.5" />
                      ) : (
                        <EyeOff className="w-3.5 h-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => onEditClick(template)}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="编辑"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {!template.is_system && (
                      confirmDelete === template.name ? (
                        <div className="flex items-center gap-1 ml-1">
                          <button
                            type="button"
                            onClick={() => handleDelete(template.name)}
                            disabled={deleteMutation.isPending}
                            className="px-2 py-1 text-[10px] bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            确认
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmDelete(null)}
                            className="px-2 py-1 text-[10px] bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                          >
                            取消
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmDelete(template.name)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="删除"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )
                    )}
                  </div>
                </div>

                {/* Status badge */}
                {!template.is_active && (
                  <span className="inline-block px-2 py-0.5 text-[10px] font-medium bg-gray-200 text-gray-600 rounded mb-2">
                    已禁用
                  </span>
                )}

                {/* Description */}
                {template.description && (
                  <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                    {template.description}
                  </p>
                )}

                {/* Channel badges and preview */}
                <NotificationPreviewCompact
                  channels={template.channels}
                  content={template.content}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
